import { baseURL } from "@/api/config";
import { type RemovableRef, useStorage } from "@vueuse/core";
import { type Ref, type UnwrapRef } from "vue";

// #region Bounce / Throttle
export function debounce(func, waitTimer, immediate) {
	let timeout = null;
	return function () {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const context = this;
		// eslint-disable-next-line prefer-rest-params
		const args = arguments;
		const later = function () {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		const callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, waitTimer);
		if (callNow) func.apply(context, args);
	};
}

export function throttle(func, waitTimer = 500) {
	let inThrottle = false;
	return function () {
		// eslint-disable-next-line prefer-rest-params
		const args = arguments;

		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const context = this;
		if (!inThrottle) {
			func.apply(context, args);
			inThrottle = true;
			setTimeout(() => {
				inThrottle = false;
			}, waitTimer);
		}
	};
}
// #endregion

// #region Simple crypt / decrypt
export function simpleCrypt(salt: string, text: string) {
	const textToChars = (text: string) =>
		text.split("").map((c) => c.codePointAt(0));
	const byteHex = (n: any) => ("0" + Number(n).toString(16)).slice(-2);
	const applySaltToChar = (code: any) =>
		textToChars(salt).reduce((a: any, b: any) => a ^ b, code);

	return text
		.split("")
		.map((element) => textToChars(element))
		.map((element) => applySaltToChar(element))
		.map((element) => byteHex(element))
		.join("");
}
export function simpleDecrypt(salt: string, encoded: string) {
	const textToChars = (text: any) =>
		text.split("").map((c: any) => c.codePointAt(0));
	const applySaltToChar = (code: any) =>
		textToChars(salt).reduce((a: any, b: any) => a ^ b, code);
	return encoded
		.match(/.{1,2}/g)
		.map((hex) => parseInt(hex, 16))
		.map((element) => applySaltToChar(element))
		.map((charCode) => String.fromCodePoint(charCode))
		.join("");
}
// #endregion

export function focusFirstElement(
	parentEl: HTMLElement,
	isFocus = true,
	isDebug = false,
) {
	// Don't invoke more than once (Before a component is destroyed)
	if (parentEl) {
		const inputListToIncludeFocusable = [
			"input:not(:disabled):not(.hidden)",
			"textarea:not(:disabled):not(.hidden)",
			"button:not(:disabled):not(.hidden)",
			"span.focusable",
			"div.focusable",
			".multiselect[tabindex]",
			"*[tabindex]:not(:disabled):not(.hidden)",
		].join(",");

		const nodeList = Array.from(
			parentEl.querySelectorAll(inputListToIncludeFocusable),
		) as HTMLElement[];
		if (nodeList?.length) {
			const addInputTabHandling = (nodeList: HTMLElement[]) => {
				const focusEl = (evt: KeyboardEvent, oppoEl: HTMLElement) => {
					// Only for first / last element
					oppoEl.focus();
					evt.preventDefault();
				};

				// First el
				nodeList[0].addEventListener("keydown", (evt: KeyboardEvent) => {
					if (evt.key === "Tab" && evt.shiftKey) {
						focusEl(evt, nodeList.at(-1) as any);
					}
				});

				// Last el
				(nodeList.at(-1) as any).addEventListener(
					"keydown",
					(evt: KeyboardEvent) => {
						if (evt.key === "Tab" && !evt.shiftKey) {
							focusEl(evt, nodeList[0]);
						}
					},
				);
			};

			if (isFocus) {
				// console.log("➕ Focusing first el", nodeList[0]);
				nodeList[0].focus();

				if (isDebug) {
					console.error("➕ Focusing first el", nodeList[0]);
				}
			}

			addInputTabHandling(nodeList);
		} else if (isDebug) {
			console.warn("No child element found for focus");
		}
	} else if (isDebug) {
		console.warn("No parent element found for focus");
	}
}

export function getCurrentDomain() {
	let url = new URL(location.href);
	try {
		url = new URL(baseURL);
	} catch {
		// ignored - could break on prod because link is not valid
	}
	return url;
}
function getUseStorage<T>(
	key: string,
	shouldParse = false,
	storage: Storage,
	defaultVal: T | null = null, // Undefined is broken (creates invalid value in store)
): Ref<T | UnwrapRef<T> | string | null | undefined> {
	const state = useStorage(key, defaultVal, storage);
	if (!shouldParse) return state;

	try {
		return ref(JSON.parse(state.value as string));
	} catch {
		console.error("Error loading key", key);
		return ref(defaultVal);
	}
}

export function getLocalStorageReac<T>(
	key: string,
	shouldParse = false,
	defaultVal?: T,
) {
	return getUseStorage(key, shouldParse, localStorage, defaultVal);
}

export function getSessionStorageReac<T>(
	key: string,
	shouldParse = false,
	defaultVal?: T,
) {
	return getUseStorage(key, shouldParse, sessionStorage, defaultVal);
}

export function setLocalStorageReac(
	key: string,
	value: any,
): RemovableRef<any> {
	let tempValue = value;
	if (typeof value !== "string") {
		tempValue = JSON.stringify(value);
	}

	// GH: https://github.com/vueuse/vueuse/issues/2193

	const state = useStorage(key, null, localStorage);
	state.value = tempValue;

	if (localStorage[key] !== tempValue) {
		localStorage[key] = tempValue;
		return ref(tempValue);
	}
	return state;
}

export function setSessionStorageReac(
	key: string,
	value: any,
): RemovableRef<any> {
	let tempValue = value;
	if (typeof value !== "string") {
		tempValue = JSON.stringify(value);
	}

	// GH: https://github.com/vueuse/vueuse/issues/2193

	const state = useStorage(key, null, sessionStorage);
	state.value = tempValue;

	if (sessionStorage[key] !== tempValue) {
		sessionStorage[key] = tempValue;
		return ref(tempValue);
	}
	return state;
}

export const InfoConsole = {
	/**
	 * Console log with colors
	 */
	l(msg: string, ...payload: any[]) {
		console.log(`:: %c${msg}`, "color:yellow; font-weight:bold", ...payload);
	},
	/**
	 * Console warn with colors
	 */
	w(msg: string, ...payload: any[]) {
		console.warn(`:: %c${msg}`, "color:yellow; font-weight:bold", ...payload);
	},
	/**
	 * Console error with colors
	 */
	e(msg: string, ...payload: any[]) {
		console.error(`:: %c${msg}`, "color:yellow; font-weight:bold", ...payload);
	},
};

export function deepToRaw<T>(val: T): T {
	return JSON.parse(JSON.stringify(val));
}

export function setupChangesFields<T>(
	original: T,
	change_ref: Ref<Partial<T>>,
): void;
export function setupChangesFields<T>(
	original: T[],
	change_ref: Ref<Partial<T>[]>,
): void;
export function setupChangesFields<T>(
	original: T | T[],
	change_ref: Ref<Partial<T>> | Ref<Partial<T>[]>,
): void {
	const originalCopy = deepToRaw(original);
	if (!Array.isArray(originalCopy)) {
		const keys = Object.keys(originalCopy);
		for (const el of keys) {
			if (Array.isArray(originalCopy[el])) {
				(change_ref as Ref<Partial<T>>).value[el] = originalCopy[el];
				continue;
			}
			if (
				typeof originalCopy[el] === "object" &&
				originalCopy[el] !== null &&
				!(originalCopy[el] instanceof Date)
			) {
				(change_ref as Ref<Partial<T>>).value[el] = originalCopy[el];
				continue;
			}
			(change_ref as Ref<Partial<T>>).value[el] = originalCopy[el];
		}
		return;
	}
	for (const el of originalCopy) {
		const keys = Object.keys(el);
		const obj: Partial<T> = {};
		for (const key of keys) {
			obj[key] = el[key];
		}
		(change_ref as Ref<Partial<T>[]>).value.push(obj);
	}
}

// deep compare object
export function deepEqual(a: any, b: any): boolean {
	if (!a && !b) return true;
	if (!a || !b) return false;
	if (typeof a !== "object" || typeof b !== "object") {
		return a === b;
	}
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) return false;
	for (const key of keysA) {
		if (!deepEqual(a[key], b[key])) return false;
	}
	return true;
}
